"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";
import { toast } from "sonner";
import API_ENDPOINTS from "@/constants/endpoints";
import { User } from "@/services/userProfile";

type NullableUser = User | null;

type UserContextType = {
  user: NullableUser;
  loading: boolean;
  isAuthenticated: boolean;
  setUser: (user: NullableUser) => void;
  logout: () => void;
};

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  setUser: () => {},
  logout: () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<NullableUser>(null);
  const [loading, setLoading] = useState(true);
  const { fetchData } = useApi();
  const router = useRouter();

  useEffect(() => {
    const loadUser = async () => {
      const token = Cookies.get("authToken");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetchData(
          API_ENDPOINTS.USER.CURRENT_USER,
          "GET",
          null,
          {},
          true,
          false
        );
        if (res?.user) {
          setUser(res.user);
        }
      } catch (err) {
        // console.error("Failed to fetch user:", err);
        Cookies.remove("authToken");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const logout = () => {    
    Cookies.remove("authToken");
    Cookies.remove("plagpro_user");
    setUser(null);
    toast.success("Logged out successfully");
    router.push("/login");
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        setUser,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
